import { useState } from 'react'
import { ArrowDownLeft, ArrowLeft, ArrowUpRight, MessageSquare, ScrollText } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import { useConversations } from '@/features/messaging/hooks'
import type { Conversation } from '@/types/messaging'

import { ComposeMessageDialog } from './compose-message-dialog'
import { MessageThread } from './message-thread'
import { ReplyComposer } from './reply-composer'

function timeAgo(dateStr: string): string {
  const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDays = Math.floor(diffHr / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function ConversationList({
  onSelect,
  className,
}: {
  onSelect: (c: Conversation) => void
  className?: string
}) {
  const { data: conversations, isLoading, isError } = useConversations()

  if (isLoading) {
    return (
      <div className={cn('flex flex-col gap-2 p-4', className)}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <p className={cn('p-6 text-center text-sm text-destructive', className)}>
        Failed to load conversations.
      </p>
    )
  }

  if (!conversations || conversations.length === 0) {
    return (
      <p className={cn('p-8 text-center text-sm text-muted-foreground', className)}>
        No conversations yet — send a text from a customer, deal, boat, or work order page.
      </p>
    )
  }

  return (
    <div className={cn('flex flex-col overflow-y-auto', className)}>
      {conversations.map((c) => (
        <button
          key={c.customer_id}
          onClick={() => onSelect(c)}
          className="flex flex-col gap-1 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/50"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="truncate font-medium">{c.customer_name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(c.last_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            {c.last_direction === 'inbound' ? (
              <ArrowDownLeft className="h-3.5 w-3.5 shrink-0 text-green-600" aria-label="inbound" />
            ) : (
              <ArrowUpRight
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                aria-label="outbound"
              />
            )}
            <span className="truncate text-sm text-muted-foreground">{c.last_body}</span>
            <Badge variant="secondary" className="ml-auto shrink-0">
              {c.message_count}
            </Badge>
          </div>
        </button>
      ))}
    </div>
  )
}

interface MessagesSidebarProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Show this customer's thread immediately; omit to start from the conversation list. */
  customerId?: string
  /** Header label for the fixed-customer case (best effort; falls back to "Messages"). */
  customerName?: string
  /** Link messages composed in this sidebar to a deal / work order (optional). */
  dealId?: string
  workOrderId?: string
}

// Right-side pop-up sidebar holding SMS conversations: either a single
// customer's thread (customerId) or a conversation list that drills into
// threads (no customerId). Template-based composing stays available via
// the header button, which opens the compose dialog on top of the sheet.
export function MessagesSidebar({
  open,
  onOpenChange,
  customerId,
  customerName,
  dealId,
  workOrderId,
}: MessagesSidebarProps) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeName, setActiveName] = useState<string | null>(null)
  const [composeOpen, setComposeOpen] = useState(false)

  // Reset the drill-in each time the sidebar opens so it always starts
  // from the conversation list. "Adjust state during render" (React docs)
  // avoids a setState-in-effect cascading render.
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (open) {
      setActiveId(null)
      setActiveName(null)
      setComposeOpen(false)
    }
  }

  const threadCustomerId = customerId ?? activeId ?? undefined
  const threadTitle = customerId ? (customerName ?? 'Messages') : (activeName ?? 'Messages')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="flex-row items-start justify-between gap-2 space-y-0 border-b p-4 pr-14">
          <div className="min-w-0">
            {threadCustomerId && !customerId && (
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 mb-1 h-7 gap-1 px-2 text-muted-foreground"
                onClick={() => {
                  setActiveId(null)
                  setActiveName(null)
                }}
              >
                <ArrowLeft className="h-3.5 w-3.5" /> All conversations
              </Button>
            )}
            <SheetTitle className="flex items-center gap-2 truncate">
              <MessageSquare className="h-4 w-4 shrink-0" />
              <span className="truncate">{threadCustomerId ? threadTitle : 'Messages'}</span>
            </SheetTitle>
            <SheetDescription className="truncate">
              {threadCustomerId
                ? 'SMS history — updates every 30s.'
                : 'Conversations across customers, newest first.'}
            </SheetDescription>
          </div>
          {threadCustomerId && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 px-2 text-muted-foreground"
              onClick={() => setComposeOpen(true)}
            >
              <ScrollText className="h-4 w-4" /> Template
            </Button>
          )}
        </SheetHeader>

        {threadCustomerId ? (
          <>
            <MessageThread customerId={threadCustomerId} className="min-h-0 flex-1 p-4" />
            <div className="border-t p-4">
              <ReplyComposer
                customerId={threadCustomerId}
                dealId={dealId}
                workOrderId={workOrderId}
              />
            </div>
          </>
        ) : (
          <ConversationList
            className="min-h-0 flex-1"
            onSelect={(c) => {
              setActiveId(c.customer_id)
              setActiveName(c.customer_name)
            }}
          />
        )}

        {threadCustomerId && (
          <ComposeMessageDialog
            open={composeOpen}
            onOpenChange={setComposeOpen}
            customerId={threadCustomerId}
            dealId={dealId}
            workOrderId={workOrderId}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}
